import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { goodsReceipts, type GoodsReceiptItem } from '@/api/goodsReceipts';
import { supplierOrders } from '@/api/orders';
import { orgs, contractors as contractorsApi, nomenclature as nomApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import ProjectSelect from '@/components/ProjectSelect';
import NomenclaturePickerModal from '@/components/NomenclaturePickerModal';
import type { GoodsBalanceRow } from '@/api/documents';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  contractor_id: string;
  contract_id: string;
  warehouse_id: string;
  supplier_order_id: string;
  supplier_doc_number: string;
  prices_include_vat: boolean;
  comment: string;
  project_id: string | null;
  items: GoodsReceiptItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  contractor_id: '',
  contract_id: '',
  warehouse_id: '',
  supplier_order_id: '',
  supplier_doc_number: '',
  prices_include_vat: false,
  comment: '',
  project_id: null,
  items: [],
};

const newRow = (
  lineNumber: number,
  defaultVatId: string,
  defaultTaxPurposeId: string | null = null,
): GoodsReceiptItem => ({
  line_number: lineNumber,
  nomenclature_id: '',
  quantity: '0',
  price: '0',
  amount_without_vat: '0',
  vat_rate_id: defaultVatId,
  vat_amount: '0',
  total_amount: '0',
  tax_purpose_id: defaultTaxPurposeId,
});

const fmt = (n: number) => n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function GoodsReceiptEditor() {
  const confirm = useConfirm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX α: фільтр org dropdown по purchases.receipt.create permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('purchases.receipt.create');
  const supplierList = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => contractorsApi.list({ limit: 500 }),
  });
  const nomList = useQuery({
    queryKey: ['nomenclature-full'],
    queryFn: () => nomApi.list({ limit: 500 }),
  });
  const vatList = useQuery({ queryKey: ['vat-rates'], queryFn: refs.vatRates });
  // 2026-06-02: tax_purposes для класифікації ВК (ПКУ ст.199).
  // Default — TAXABLE («Господарська діяльність, оподатковувана ПДВ»).
  const taxPurposesList = useQuery({ queryKey: ['tax-purposes'], queryFn: refs.taxPurposes });
  const defaultTaxPurposeId = taxPurposesList.data?.find((tp) => tp.code === 'TAXABLE')?.id ?? null;
  const docQuery = useQuery({
    queryKey: ['goods-receipt', id],
    queryFn: () => goodsReceipts.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const numberPreview = useNumberPreview('doc.goods_receipts', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);
  // 2026-06-02: Picker (catalog mode) для додавання товару.
  const [pickerOpen, setPickerOpen] = useState(false);

  const warehousesQ = useQuery({
    queryKey: ['warehouses', form.organization_id],
    queryFn: () => refs.warehouses(form.organization_id),
    enabled: !!form.organization_id,
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', form.organization_id, form.contractor_id],
    queryFn: () => refs.contracts({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });
  const supplierOrdersQ = useQuery({
    queryKey: ['supplier-orders-active', form.organization_id, form.contractor_id],
    queryFn: () => supplierOrders.list({
      organization_id: form.organization_id,
      contractor_id: form.contractor_id,
      posted: true,
      limit: 100,
    }),
    enabled: !!form.organization_id && !!form.contractor_id,
  });

  // выбор default НДС 20%
  const defaultVatId = useMemo(() => {
    const v = vatList.data?.find((x) => x.rate_percent === 20);
    return v?.id ?? '';
  }, [vatList.data]);
  // 2026-06-02: ховаємо колонки ПДВ якщо org НЕ платник.
  // EXEMPT-rate автоматично проставляється у newRow → бухгалтер єдинника
  // без ПДВ взагалі не бачить колонок ПДВ/Призн. ПДВ/Сума ПДВ.
  const currentOrg = useMemo(
    () => orgList.data?.items.find((o) => o.id === form.organization_id),
    [orgList.data, form.organization_id],
  );
  const showVatColumns = currentOrg?.is_vat_payer ?? true;
  // Для неплатників — VAT_0 rate (буде в payload, але колонки сховані).
  const exemptVatId = useMemo(() => {
    const v = vatList.data?.find((x) => x.code === 'EXEMPT' || x.code === 'VAT_0');
    return v?.id ?? '';
  }, [vatList.data]);
  const effectiveDefaultVatId = showVatColumns ? defaultVatId : exemptVatId;
  // 2026-06-02: карта nomenclature_id -> base_uom_id для auto-fill.
  const baseUomByNomId = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of nomList.data?.items ?? []) {
      if (n.base_uom_id) m.set(n.id, n.base_uom_id);
    }
    return m;
  }, [nomList.data]);
  // Етап 2.1: карта nomenclature_id -> vat_rate_id (ставка ПДВ з картки).
  const vatRateByNomId = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of nomList.data?.items ?? []) {
      if (n.vat_rate_id) m.set(n.id, n.vat_rate_id);
    }
    return m;
  }, [nomList.data]);

  // Підфіча #4 (2026-06-08): серії + характеристики у рядках приходу.
  // Карти nomenclature_id -> use_series / use_characteristics. Для рядків з
  // прапором — показуємо row detail з селектом серії / характеристики.
  const useSeriesMap = useMemo(() => {
    const m = new Map<string, boolean>();
    nomList.data?.items.forEach((n) => m.set(n.id, !!n.use_series));
    return m;
  }, [nomList.data]);
  const useCharMap = useMemo(() => {
    const m = new Map<string, boolean>();
    nomList.data?.items.forEach((n) => m.set(n.id, !!n.use_characteristics));
    return m;
  }, [nomList.data]);

  // Паралельні запити серій по номенклатурах рядків з use_series=true.
  const seriesNomIds = useMemo(() => {
    const s = new Set<string>();
    for (const it of form.items) {
      if (it.nomenclature_id && useSeriesMap.get(it.nomenclature_id)) s.add(it.nomenclature_id);
    }
    return Array.from(s);
  }, [form.items, useSeriesMap]);
  const seriesQueries = useQueries({
    queries: seriesNomIds.map((nid) => ({
      queryKey: ['nomenclature-series', nid],
      queryFn: () => refs.nomenclatureSeries(nid),
      enabled: !!nid,
      staleTime: 60 * 1000,
    })),
  });
  const seriesByNom = useMemo(() => {
    const m: Record<string, { id: string; name: string; code: string | null }[]> = {};
    seriesNomIds.forEach((nid, idx) => {
      const q = seriesQueries[idx];
      if (q?.data) m[nid] = q.data;
    });
    return m;
  }, [seriesNomIds, seriesQueries]);

  // Паралельні запити характеристик по номенклатурах рядків з use_characteristics=true.
  const charNomIds = useMemo(() => {
    const s = new Set<string>();
    for (const it of form.items) {
      if (it.nomenclature_id && useCharMap.get(it.nomenclature_id)) s.add(it.nomenclature_id);
    }
    return Array.from(s);
  }, [form.items, useCharMap]);
  const charQueries = useQueries({
    queries: charNomIds.map((nid) => ({
      queryKey: ['nomenclature-characteristics', nid],
      queryFn: () => refs.nomenclatureCharacteristics(nid),
      enabled: !!nid,
      staleTime: 60 * 1000,
    })),
  });
  const charByNom = useMemo(() => {
    const m: Record<string, { id: string; name: string; code: string | null }[]> = {};
    charNomIds.forEach((nid, idx) => {
      const q = charQueries[idx];
      if (q?.data) m[nid] = q.data;
    });
    return m;
  }, [charNomIds, charQueries]);

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f,
        organization_id: firstOrg,
        items: f.items.length ? f.items : [newRow(1, effectiveDefaultVatId, defaultTaxPurposeId)],
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        contractor_id: d.contractor_id,
        contract_id: d.contract_id,
        warehouse_id: d.warehouse_id,
        supplier_order_id: d.supplier_order_id ?? '',
        supplier_doc_number: d.supplier_doc_number ?? '',
        prices_include_vat: d.prices_include_vat,
        comment: d.comment ?? '',
        project_id: d.project_id ?? null,
        items: d.items.map((it) => ({
          line_number: it.line_number,
          nomenclature_id: it.nomenclature_id,
          series_id: it.series_id ?? null,
          characteristic_id: it.characteristic_id ?? null,
          quantity: String(it.quantity),
          price: String(it.price),
          amount_without_vat: String(it.amount_without_vat),
          vat_rate_id: it.vat_rate_id,
          vat_amount: String(it.vat_amount),
          total_amount: String(it.total_amount),
          tax_purpose_id: it.tax_purpose_id ?? null,
          uom_id: it.uom_id ?? null,
        })),
      });
    }
  }, [isNew, docQuery.data, availableOrgs, defaultVatId, defaultTaxPurposeId]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const totalWithoutVat = form.items.reduce((s, e) => s + Number(e.amount_without_vat || 0), 0);
  const totalVat = form.items.reduce((s, e) => s + Number(e.vat_amount || 0), 0);
  const totalAmount = totalWithoutVat + totalVat;

  const create = useMutation({
    mutationFn: () => goodsReceipts.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      warehouse_id: form.warehouse_id,
      supplier_order_id: form.supplier_order_id || null,
      supplier_doc_number: form.supplier_doc_number || null,
      prices_include_vat: form.prices_include_vat,
      comment: form.comment || null,
      project_id: form.project_id,
      items: form.items,
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['goods-receipts'] });
      navigate(`/goods-receipts/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => goodsReceipts.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      contractor_id: form.contractor_id,
      contract_id: form.contract_id || null,
      warehouse_id: form.warehouse_id,
      supplier_order_id: form.supplier_order_id || null,
      supplier_doc_number: form.supplier_doc_number || null,
      prices_include_vat: form.prices_include_vat,
      comment: form.comment || null,
      project_id: form.project_id,
      items: form.items,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['goods-receipt', id] }),
    onError: (e) => setError(extractError(e)),
  });
  const post = useMutation({
    mutationFn: () => goodsReceipts.post(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-receipt', id] });
      qc.invalidateQueries({ queryKey: ['goods-receipts'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => goodsReceipts.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-receipt', id] });
      qc.invalidateQueries({ queryKey: ['goods-receipts'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => goodsReceipts.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['goods-receipts'] });
      navigate('/goods-receipts');
    },
  });

  function setItem(i: number, patch: Partial<GoodsReceiptItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        // авто-пересчёт суммы и НДС
        const qty = Number(next.quantity) || 0;
        const price = Number(next.price) || 0;
        const vat = vatList.data?.find((v) => v.id === next.vat_rate_id);
        const rate = vat?.rate_percent ?? 0;
        if ('quantity' in patch || 'price' in patch || 'vat_rate_id' in patch) {
          const awv = qty * price;
          const vatAmt = (awv * rate) / 100;
          next.amount_without_vat = awv.toFixed(2);
          next.vat_amount = vatAmt.toFixed(2);
          next.total_amount = (awv + vatAmt).toFixed(2);
        }
        return next;
      }),
    }));
  }
  function addRow() {
    setForm((f) => ({
      ...f,
      items: [...f.items, newRow(f.items.length + 1, effectiveDefaultVatId, defaultTaxPurposeId)],
    }));
  }
  function addRowFromPicker(picked: GoodsBalanceRow) {
    setForm((f) => {
      const nextLine = f.items.length + 1;
      const row: GoodsReceiptItem = {
        ...newRow(nextLine, effectiveDefaultVatId, defaultTaxPurposeId),
        nomenclature_id: picked.nomenclature_id,
        // Auto-fill uom_id з base_uom_id (2026-06-02).
        uom_id: baseUomByNomId.get(picked.nomenclature_id) ?? null,
        // Етап 2.1: ставка ПДВ з картки (fallback — дефолт документа).
        vat_rate_id: vatRateByNomId.get(picked.nomenclature_id) ?? effectiveDefaultVatId,
      };
      return { ...f, items: [...f.items, row] };
    });
    setPickerOpen(false);
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i).map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
  }

  // Зберегти і одразу провести (на новому — create→post, на збереженому — post).
  async function onSaveAndPost() {
    setError(null);
    try {
      if (isNew) {
        const doc = await create.mutateAsync();
        await goodsReceipts.post(doc.id);
        qc.invalidateQueries({ queryKey: ['goods-receipts'] });
      } else {
        await update.mutateAsync();
        await post.mutateAsync();
      }
    } catch (e) {
      setError(extractError(e));
    }
  }

  // Загружает остаток (quantity - quantity_received) из заказа поставщику.
  async function fillFromOrder() {
    if (!form.supplier_order_id) return;
    try {
      const order = await supplierOrders.get(form.supplier_order_id);
      const newItems: GoodsReceiptItem[] = order.items
        .map((it, idx) => {
          const remaining = Number(it.quantity) - Number(it.quantity_received ?? 0);
          if (remaining <= 0) return null;
          const price = Number(it.price);
          const awv = remaining * price;
          const vat = vatList.data?.find((v) => v.id === it.vat_rate_id);
          const rate = vat?.rate_percent ?? 0;
          const vatAmt = (awv * rate) / 100;
          return {
            line_number: idx + 1,
            nomenclature_id: it.nomenclature_id,
            quantity: String(remaining),
            price: String(price),
            amount_without_vat: awv.toFixed(2),
            vat_rate_id: it.vat_rate_id,
            vat_amount: vatAmt.toFixed(2),
            total_amount: (awv + vatAmt).toFixed(2),
          };
        })
        .filter((x): x is GoodsReceiptItem => x !== null);
      if (newItems.length === 0) {
        alert('Замовлення повністю отримано — немає що додавати.');
        return;
      }
      setForm((f) => ({ ...f, items: newItems }));
    } catch (e) {
      setError(extractError(e));
    }
  }

  // UX α: warning якщо новий документ а user не має permission у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>{t('gr.new')}</h2>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати надходження товарів у жодній з
            ваших організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/goods-receipts')}>{t('common.cancel')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? t('gr.new') : `${t('gr.title')}: ${form.document_number}`}
          {isPosted && <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>✓ {t('mo.posted_yes')}</span>}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/goods-receipts')}
          onSave={() => (isNew ? create.mutate() : update.mutate())}
          saveDisabled={create.isPending || update.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={onSaveAndPost}
          postDisabled={!isNew && form.items.length === 0}
          postPending={create.isPending || update.isPending || post.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => {
            confirm(t('common.confirm_delete')).then((ok) => { if (ok) remove.mutate(); });
          }}
          deletePending={remove.isPending}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>{t('gr.organization')}</label>
          <select
            value={form.organization_id}
            onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
        </div>
        <div className="field">
          <label>{t('gr.number')}</label>
          <input
            value={form.document_number}
            onChange={(e) => setForm({ ...form, document_number: e.target.value })}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
            disabled={isPosted}
          />
        </div>
        <div className="field">
          <label>{t('gr.date')}</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={isPosted}
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>{t('gr.contractor')}</label>
          <select
            value={form.contractor_id}
            onChange={(e) => setForm({ ...form, contractor_id: e.target.value, contract_id: '' })}
            disabled={isPosted}
          >
            <option value="">—</option>
            {supplierList.data?.items.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>
            {t('gr.contract')}{' '}
            <small style={{ color: 'var(--muted)' }}>
              (необов'язково — буде створено основний автоматично)
            </small>
          </label>
          <select
            value={form.contract_id}
            onChange={(e) => setForm({ ...form, contract_id: e.target.value })}
            disabled={isPosted || !form.contractor_id}
          >
            <option value="">без договору</option>
            {contractsQ.data?.map((c) => (
              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>{t('gr.warehouse')}</label>
          <select
            value={form.warehouse_id}
            onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
            disabled={isPosted}
          >
            <option value="">—</option>
            {warehousesQ.data?.map((w) => (
              <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="field">
        <label>{t('gr.supplier_doc')}</label>
        <input
          value={form.supplier_doc_number}
          onChange={(e) => setForm({ ...form, supplier_doc_number: e.target.value })}
          disabled={isPosted}
        />
      </div>

      {!!form.contractor_id && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr auto', gap: '0.75rem' }}>
          <div className="field">
            <label>На підставі замовлення постачальнику (необов'язково)</label>
            <select value={form.supplier_order_id}
                    onChange={(e) => setForm({ ...form, supplier_order_id: e.target.value })}
                    disabled={isPosted}>
              <option value="">— (без замовлення)</option>
              {supplierOrdersQ.data?.items
                .filter((o) => o.state !== 'COMPLETED' && o.state !== 'CANCELLED')
                .map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.document_number} від {new Date(o.document_date).toLocaleDateString('uk-UA')}
                    {' '}({o.state})
                  </option>
                ))}
            </select>
          </div>
          {!isPosted && form.supplier_order_id && (
            <div className="field" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button onClick={() => void fillFromOrder()}>
                ⟳ Заповнити з замовлення
              </button>
            </div>
          )}
        </div>
      )}
      <div className="field">
        <label>Проект / напрям</label>
        <ProjectSelect
          value={form.project_id}
          organizationId={form.organization_id}
          onChange={(v) => setForm({ ...form, project_id: v })}
          disabled={isPosted} />
      </div>


      <h3 style={{ marginTop: '1.5rem' }}>{t('gr.items')}</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
            <th style={{ padding: '0.5rem', width: 40 }}>#</th>
            <th style={{ padding: '0.5rem' }}>{t('gr.nomenclature')}</th>
            <th style={{ padding: '0.5rem', width: 90 }}>{t('gr.qty')}</th>
            <th style={{ padding: '0.5rem', width: 110 }}>{t('gr.price')}</th>
            <th style={{ padding: '0.5rem', width: 130 }}>
              {showVatColumns ? t('gr.amount_without_vat') : 'Сума'}
            </th>
            {showVatColumns && (
              <>
                <th style={{ padding: '0.5rem', width: 100 }}>{t('gr.vat')}</th>
                <th style={{ padding: '0.5rem', width: 220 }}
                    title="Призначення ПДВ — класифікація ВК для пропорційного розподілу (ПКУ ст.199)">
                  Призн. ПДВ
                </th>
                <th style={{ padding: '0.5rem', width: 110 }}>{t('gr.vat_amount')}</th>
                <th style={{ padding: '0.5rem', width: 120 }}>{t('gr.total')}</th>
              </>
            )}
            <th style={{ padding: '0.5rem', width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => {
            const showSeries = !!it.nomenclature_id && useSeriesMap.get(it.nomenclature_id);
            const showChar = !!it.nomenclature_id && useCharMap.get(it.nomenclature_id);
            return (
            <Fragment key={i}>
            <tr style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.5rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.5rem' }}>
                <select
                  value={it.nomenclature_id}
                  onChange={(e) => {
                    const nomId = e.target.value;
                    setItem(i, {
                      nomenclature_id: nomId,
                      // Auto-fill uom_id (2026-06-02).
                      uom_id: baseUomByNomId.get(nomId) ?? null,
                      // Етап 2.1: ставка ПДВ з картки (пересчёт vat_amount у setItem).
                      vat_rate_id: vatRateByNomId.get(nomId) ?? it.vat_rate_id,
                      // Підфіча #4: скидаємо серію/характеристику при зміні номенклатури.
                      series_id: null,
                      characteristic_id: null,
                    });
                  }}
                  disabled={isPosted}
                >
                  <option value="">—</option>
                  {nomList.data?.items.map((n) => (
                    <option key={n.id} value={n.id}>{n.code} — {n.name}</option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.001" value={it.quantity}
                  onChange={(e) => setItem(i, { quantity: e.target.value })}
                  disabled={isPosted} />
              </td>
              <td style={{ padding: '0.5rem' }}>
                <input type="number" step="0.01" value={it.price}
                  onChange={(e) => setItem(i, { price: e.target.value })}
                  disabled={isPosted} />
              </td>
              <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                {fmt(Number(it.amount_without_vat))}
              </td>
              {showVatColumns && (
                <>
                  <td style={{ padding: '0.5rem' }}>
                    <select
                      value={it.vat_rate_id}
                      onChange={(e) => setItem(i, { vat_rate_id: e.target.value })}
                      disabled={isPosted}
                    >
                      {vatList.data?.map((v) => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                  </td>
                  {/* 2026-06-02: tax_purpose dropdown — class. ВК для ст.199. */}
                  <td style={{ padding: '0.5rem' }}>
                    <select
                      value={it.tax_purpose_id ?? ''}
                      onChange={(e) => setItem(i, {
                        tax_purpose_id: e.target.value || null,
                      })}
                      disabled={isPosted}
                      style={{ width: '100%' }}
                      title="Призначення ПДВ-кредиту"
                    >
                      <option value="">— не вказано —</option>
                      {taxPurposesList.data?.map((tp) => (
                        <option key={tp.id} value={tp.id}>{tp.name_uk}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                    {fmt(Number(it.vat_amount))}
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>
                    {fmt(Number(it.total_amount))}
                  </td>
                </>
              )}
              <td style={{ padding: '0.5rem' }}>
                {!isPosted && (
                  <button onClick={() => removeRow(i)} className="danger" style={{ padding: '0.3rem 0.6rem' }}>×</button>
                )}
              </td>
            </tr>
            {(showSeries || showChar) && (
              <tr style={{ background: '#fefce8', fontSize: '0.8rem' }}>
                <td></td>
                <td colSpan={showVatColumns ? 9 : 5} style={{ padding: '0.3rem 0.5rem' }}>
                  <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                    {showSeries && (
                      <label>
                        <span style={{ color: '#854d0e', marginRight: '0.3rem' }}>Серія:</span>
                        <select
                          value={it.series_id ?? ''}
                          onChange={(e) => setItem(i, { series_id: e.target.value || null })}
                          disabled={isPosted}
                          style={{ minWidth: 220 }}
                        >
                          <option value="">— оберіть серію —</option>
                          {(seriesByNom[it.nomenclature_id] ?? []).map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.code ? `${s.code} — ` : ''}{s.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    {showChar && (
                      <label>
                        <span style={{ color: '#854d0e', marginRight: '0.3rem' }}>Характеристика:</span>
                        <select
                          value={it.characteristic_id ?? ''}
                          onChange={(e) => setItem(i, { characteristic_id: e.target.value || null })}
                          disabled={isPosted}
                          style={{ minWidth: 220 }}
                        >
                          <option value="">— оберіть характеристику —</option>
                          {(charByNom[it.nomenclature_id] ?? []).map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.code ? `${c.code} — ` : ''}{c.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                </td>
              </tr>
            )}
            </Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600 }}>
            <td colSpan={4} style={{ padding: '0.5rem', textAlign: 'right' }}>{t('gr.total')}:</td>
            <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalWithoutVat)}</td>
            {showVatColumns && (
              <>
                <td></td>
                <td></td>{/* tax_purpose column */}
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalVat)}</td>
                <td style={{ padding: '0.5rem', textAlign: 'right' }}>{fmt(totalAmount)}</td>
              </>
            )}
            <td></td>
          </tr>
        </tfoot>
      </table>
      {!isPosted && (
        <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
          <button
            className="primary"
            onClick={() => setPickerOpen(true)}
            disabled={!form.organization_id}
            title={!form.organization_id
              ? 'Спершу оберіть юрособу'
              : 'Підбір товару з довідника номенклатури'}
          >
            + Додати товар з довідника
          </button>
          <button onClick={addRow}>+ Порожній рядок</button>
        </div>
      )}
      {error && <div className="error">{error}</div>}
      {pickerOpen && form.organization_id && (
        <NomenclaturePickerModal
          organizationId={form.organization_id}
          documentDate={form.document_date}
          onPick={addRowFromPicker}
          onClose={() => setPickerOpen(false)}
          catalogMode
        />
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as {
      response?: {
        data?: { detail?: string | Array<{ loc?: unknown[]; msg?: string }> }
      }
    }).response;
    const detail = r?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      return detail.slice(0, 3).map((item) => {
        const field = Array.isArray(item.loc) && item.loc.length > 1
          ? String(item.loc[item.loc.length - 1]) : 'поле';
        return `${field}: ${item.msg ?? 'невідома помилка'}`;
      }).join('; ');
    }
    return 'Error';
  }
  return String(e);
}
